import {
  Eye,
  Play,
  CheckCircle2,
  Users,
  UserCheck,
  Percent,
  Calendar,
  RefreshCw,
  Filter,
  Trophy,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { useVideosSueroterapia } from '../hooks/useSueroterapia'
import type { RankingItem, VideoFunnelItem } from '../types'
import { AZUL, VERDE, MORADO, ROSA, KpiCard, formatoFecha } from './KpiCard'

interface FunnelCardProps {
  items: VideoFunnelItem[]
}

function FunnelCard({ items }: FunnelCardProps) {
  const nf = (n: number) => n.toLocaleString('es-EC')
  // Base del embudo: la primera etapa (Mostrado) es el 100%.
  const base = items.length > 0 ? items[0].count : 0
  return (
    <Card className="border border-gray-300 dark:border-gray-700 flex flex-col">
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Filter className="h-4 w-4" style={{ color: AZUL }} />
          Embudo de visualización
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        {items.length > 0 ? (
          items.map((it, idx) => {
            const pct = base > 0 ? (it.count / base) * 100 : 0
            return (
              <div key={`${it.label}-${idx}`} className="space-y-1">
                <div className="flex items-center justify-between text-sm gap-3">
                  <span className="font-medium text-foreground truncate" title={it.label}>
                    {it.label}
                  </span>
                  <span className="text-xs font-semibold shrink-0 text-black dark:text-white">
                    {nf(it.count)}{' '}
                    <span className="text-muted-foreground font-normal">({pct.toFixed(0)}%)</span>
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-800">
                  <div
                    className="h-2 rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, backgroundColor: AZUL }}
                  />
                </div>
              </div>
            )
          })
        ) : (
          <p className="text-xs text-muted-foreground py-4 text-center">Sin datos en el período.</p>
        )}
      </CardContent>
    </Card>
  )
}

interface TopViewersCardProps {
  items: RankingItem[]
}

function TopViewersCard({ items }: TopViewersCardProps) {
  const nf = (n: number) => n.toLocaleString('es-EC')
  const max = items.reduce((m, it) => Math.max(m, it.count), 0) || 1
  return (
    <Card className="border border-gray-300 dark:border-gray-700 flex flex-col">
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Trophy className="h-4 w-4" style={{ color: MORADO }} />
          Espectadores más activos
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        {items.length > 0 ? (
          items.slice(0, 8).map((it, idx) => {
            const pct = (it.count / max) * 100
            return (
              <div key={`${it.label}-${idx}`} className="space-y-1">
                <div className="flex items-center justify-between text-sm gap-3">
                  <span className="font-medium text-foreground truncate" title={it.label}>
                    {it.label || '—'}
                  </span>
                  <span className="text-xs font-semibold shrink-0" style={{ color: MORADO }}>
                    {nf(it.count)}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-800">
                  <div
                    className="h-2 rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, backgroundColor: MORADO }}
                  />
                </div>
              </div>
            )
          })
        ) : (
          <p className="text-xs text-muted-foreground py-4 text-center">
            Sin espectadores en el período.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

interface VideosTabProps {
  dias: number
}

export function VideosTab({ dias }: VideosTabProps) {
  const { data, isLoading, error } = useVideosSueroterapia(dias)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <p className="text-destructive text-sm text-center max-w-md">
          No se pudo cargar el reporte de videos. Verifique que la integración de Sueroterapia
          esté configurada y activa, e intente nuevamente.
        </p>
      </div>
    )
  }

  const nf = (n: number) => n.toLocaleString('es-EC')
  const { kpis } = data

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="text-xs flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {formatoFecha(data.rango.desde)} — {formatoFecha(data.rango.hasta)}
        </Badge>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <RefreshCw className="h-3 w-3" /> datos en vivo
        </span>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard
          icon={Eye}
          label="Impresiones"
          value={nf(kpis.impresiones)}
          sub="Videos mostrados"
          color={AZUL}
        />
        <KpiCard
          icon={Play}
          label="Reproducciones"
          value={nf(kpis.plays)}
          sub="Videos reproducidos"
          color={VERDE}
        />
        <KpiCard
          icon={CheckCircle2}
          label="Completados"
          value={nf(kpis.completados)}
          sub="Reproducciones finalizadas"
          color={MORADO}
        />
        <KpiCard
          icon={Users}
          label="Espectadores únicos"
          value={nf(kpis.unique_viewers)}
          sub="Usuarios que reprodujeron"
          color={AZUL}
        />
        <KpiCard
          icon={UserCheck}
          label="Completadores únicos"
          value={nf(kpis.unique_completers)}
          sub="Usuarios que finalizaron"
          color={VERDE}
        />
        <KpiCard
          icon={Percent}
          label="Tasa de finalización"
          value={`${kpis.pct_finalizacion.toFixed(1)}%`}
          sub="Completados sobre reproducciones"
          color={ROSA}
        />
      </div>

      {/* Embudo + Top viewers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FunnelCard items={data.funnel} />
        <TopViewersCard items={data.top_viewers} />
      </div>
    </div>
  )
}
